import type { CartLine } from "@/store/cart";
import type { OrderLineOutput, OrderOutput } from "@/api/online-ordering-service/model";
import type { UiMenuItem } from "@/hooks/useMenu";
import { localizeValue } from "@/lib/fulfillment";

/**
 * Rebuild cart lines from a past/unpaid order's lines, against the CURRENT menu.
 * Items no longer on the menu are skipped (returned in `skipped`).
 *
 * Pricing: the cart total is `price * qty`, so `price` must be the fully
 * configured per-unit price. The order line already records that — use its
 * recorded line subtotal (base + variants + modifiers, pre-tax) ÷ qty. Do NOT
 * re-add variant/modifier adjustments on top of `unitPrice` (it already includes
 * them) — that was the reorder double-pricing bug.
 */
export function buildCartLinesFromOrder(
  order: OrderOutput,
  menuItems: UiMenuItem[],
  lang: string,
): { lines: CartLine[]; skipped: number } {
  const lines: CartLine[] = [];
  let skipped = 0;

  for (const l of (order.orderLines ?? []) as OrderLineOutput[]) {
    const menuItem = menuItems.find((m) => m.id === l.itemId);
    if (!menuItem) {
      skipped++;
      continue;
    }
    const selectedModifiers = l.selectedModifiers ?? [];
    const selectedVariants = l.selectedVariants ?? [];

    const modsLabel: string[] = [];
    selectedVariants.forEach((v) => {
      const lbl = localizeValue(v.variantOptionName, lang);
      if (lbl) modsLabel.push(lbl);
    });
    selectedModifiers.forEach((m) => {
      const lbl = localizeValue(m.modifierOptionName, lang);
      if (lbl) modsLabel.push(lbl);
    });

    const qty = l.quantity ?? 1;
    const variantAdjust = selectedVariants.reduce((s, v) => s + (v.priceAdjustment ?? 0), 0);
    const modifiersTotal = selectedModifiers.reduce(
      (s, m) => s + (m.unitPrice ?? 0) * (m.quantity ?? 1),
      0,
    );
    const recordedSubtotal = l.pricingSummary?.subtotalAmount;
    const unitPrice =
      recordedSubtotal != null && qty > 0
        ? recordedSubtotal / qty
        : (l.unitPrice ?? menuItem.price + variantAdjust + modifiersTotal);

    lines.push({
      id: `${l.itemId}-${Date.now()}-${lines.length}`,
      itemId: l.itemId,
      name: localizeValue(l.itemName, lang) || menuItem.name,
      categoryId: menuItem.cat,
      categoryName: menuItem.categoryName,
      categoryNameRaw: menuItem.categoryNameRaw,
      price: unitPrice,
      qty,
      tone: menuItem.tone,
      selectedModifiers,
      selectedVariants,
      modsLabel,
      notes: l.notes ?? "",
    });
  }

  return { lines, skipped };
}
